import React, { useEffect, useState, useCallback, useRef } from "react";
import { ref, get, push, set } from "firebase/database";
import { db } from "../firebase";
import { useNavigate } from "react-router-dom";

// Slug generator
function slugify(s) {
  return s.toLowerCase().trim().replace(/\s+/g, "-").replace(/[^a-z0-9\-]/g, "");
}

export default function AdminDashboard() {
  const [areas, setAreas] = useState([]);
  const [name, setName] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [showForm, setShowForm] = useState(true);
  const [message, setMessage] = useState("");
  const navigate = useNavigate();
  const dataFetchedRef = useRef(false);

  // Fetch areas - OPTIMIZED: using get() instead of onValue()
  const fetchAreas = useCallback(async () => {
    setLoading(true);
    try {
      const areasRef = ref(db, "areas");
      const snapshot = await get(areasRef);
      const data = snapshot.val() || {};
      const arr = Object.entries(data).map(([id, v]) => ({ 
        id, 
        meta: v.meta,
        stats: {
          customerCount: Object.keys(v.customers || {}).length,
          entryCount: Object.keys(v.entries || {}).length
        }
      }));
      setAreas(arr);
    } catch (err) {
      console.error("Error fetching areas:", err);
      setMessage("❌ Error loading areas");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!dataFetchedRef.current) {
      dataFetchedRef.current = true;
      fetchAreas();
    }
  }, [fetchAreas]);

  const createArea = async (e) => {
    e.preventDefault();
    if (!name || !username || !password) {
      setMessage("❌ Please fill all fields before creating an area.");
      return;
    }

    setCreating(true);
    const slug = slugify(name);
    const newRef = push(ref(db, "areas"));

    try {
      await set(newRef, {
        meta: { name, slug, username, password },
        customers: {},
        entries: {},
      });

      setName("");
      setUsername("");
      setPassword("");
      setMessage(`✅ Area "${name}" created successfully!`);
      
      // Refresh the areas list
      await fetchAreas();
      
      setTimeout(() => setMessage(""), 3000);
    } catch (err) {
      console.error("Error creating area:", err);
      setMessage("❌ Error creating area");
    } finally {
      setCreating(false);
    }
  };

  const openArea = async (slug, areaId) => {
    try {
      await set(ref(db, `sessions/areas/${areaId}`), {
        loggedIn: true,
        slug,
        timestamp: Date.now(),
      });
      navigate(`/area/${slug}`);
    } catch (err) {
      console.error("Error opening area:", err);
      setMessage("❌ Error opening area");
    }
  };

  const handleLogout = async () => {
    try {
      await set(ref(db, "sessions/admin/loggedIn"), false);
      window.location.href = "/";
    } catch (err) {
      console.error("Error logging out:", err);
      window.location.href = "/";
    }
  };

  // Filter areas based on search
  const filteredAreas = areas.filter(area => 
    area.meta?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    area.meta?.username?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Stats
  const totalAreas = areas.length;
  const totalCustomers = areas.reduce((sum, area) => sum + (area.stats?.customerCount || 0), 0);
  const totalEntries = areas.reduce((sum, area) => sum + (area.stats?.entryCount || 0), 0);

  return (
    <div className="admin-dashboard">
      {/* Header */}
      <header className="admin-header">
        <div className="header-content">
          <div className="logo-section">
            <div className="logo-icon">🏪</div>
            <div className="logo-text">
              <h1>Jauhari Enterprises</h1>
              <p>Admin Dashboard</p>
            </div>
          </div>
          <button onClick={handleLogout} className="logout-btn">
            🚪 Logout
          </button>
        </div>
      </header>

      {/* Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon">🏘️</div>
          <div className="stat-info">
            <div className="stat-value">{totalAreas}</div>
            <div className="stat-label">Total Areas</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">👥</div>
          <div className="stat-info">
            <div className="stat-value">{totalCustomers}</div>
            <div className="stat-label">Total Customers</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">📝</div>
          <div className="stat-info">
            <div className="stat-value">{totalEntries}</div>
            <div className="stat-label">Total Entries</div>
          </div>
        </div>
      </div>

      <div className="admin-main">
        {/* Create Area Section - Collapsible on mobile */}
        <div className={`create-area-card ${showForm ? 'expanded' : ''}`}>
          <div className="card-header" onClick={() => setShowForm(!showForm)}>
            <h2>➕ Create New Area</h2>
            <button className="toggle-btn">{showForm ? '−' : '+'}</button>
          </div>
          
          {showForm && (
            <form onSubmit={createArea} className="create-form">
              <div className="form-group">
                <label>🏷️ Area Name</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g., Ayodhya North"
                  disabled={creating}
                />
              </div>

              <div className="form-group">
                <label>👤 Username</label>
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="e.g., ayodhya-north"
                  disabled={creating}
                />
              </div>

              <div className="form-group">
                <label>🔒 Password</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  disabled={creating}
                />
              </div>

              <button type="submit" className="create-btn" disabled={creating}>
                {creating ? "Creating..." : "Create Area"}
              </button>
            </form>
          )}
        </div>

        {/* Areas List Section */}
        <div className="areas-list-card">
          <div className="card-header">
            <h2>📋 Existing Areas</h2>
            <div className="search-box">
              <input
                type="text"
                placeholder="🔍 Search areas..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          {loading ? (
            <div className="loading-state">
              <div className="spinner"></div>
              <p>Loading areas...</p>
            </div>
          ) : (
            <>
              {/* Desktop Table View */}
              <div className="desktop-view">
                <table className="areas-table">
                  <thead>
                    <tr>
                      <th>Area Name</th>
                      <th>Username</th>
                      <th>Slug</th>
                      <th>Customers</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredAreas.length === 0 ? (
                      <tr>
                        <td colSpan="5" className="empty-state">
                          {searchTerm ? "No areas found matching your search" : "No areas created yet"}
                        </td>
                      </tr>
                    ) : (
                      filteredAreas.map((area) => (
                        <tr key={area.id}>
                          <td className="area-name">{area.meta?.name}</td>
                          <td>{area.meta?.username}</td>
                          <td className="slug">{area.meta?.slug}</td>
                          <td className="customer-count">{area.stats?.customerCount || 0}</td>
                          <td>
                            <button
                              onClick={() => openArea(area.meta?.slug, area.id)}
                              className="open-area-btn"
                            >
                              Open Area →
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Mobile Card View */}
              <div className="mobile-view">
                {filteredAreas.length === 0 ? (
                  <div className="empty-state">
                    {searchTerm ? "No areas found" : "No areas created yet"}
                  </div>
                ) : (
                  filteredAreas.map((area) => (
                    <div key={area.id} className="area-card">
                      <div className="area-card-header">
                        <h3>{area.meta?.name}</h3>
                        <span className="customer-badge">{area.stats?.customerCount || 0} customers</span>
                      </div>
                      <div className="area-card-details">
                        <div className="detail-row">
                          <span>Username:</span>
                          <strong>{area.meta?.username}</strong>
                        </div>
                        <div className="detail-row">
                          <span>Slug:</span>
                          <code>{area.meta?.slug}</code>
                        </div>
                      </div>
                      <button
                        onClick={() => openArea(area.meta?.slug, area.id)}
                        className="open-area-btn-mobile"
                      >
                        Open Area →
                      </button>
                    </div>
                  ))
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Message Toast */}
      {message && (
        <div className={`message-toast ${message.includes('✅') ? 'success' : 'error'}`}>
          {message}
        </div>
      )}

      <style jsx>{`
        .admin-dashboard {
          min-height: 100vh;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          padding: 20px;
        }

        /* Header */
        .admin-header {
          background: rgba(255,255,255,0.95);
          border-radius: 15px;
          padding: 15px 25px;
          margin-bottom: 25px;
          box-shadow: 0 5px 20px rgba(0,0,0,0.1);
        }

        .header-content {
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 15px;
        }

        .logo-section {
          display: flex;
          align-items: center;
          gap: 15px;
        }

        .logo-icon {
          font-size: 40px;
          background: linear-gradient(135deg, #667eea, #764ba2);
          width: 50px;
          height: 50px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 12px;
        }

        .logo-text h1 {
          margin: 0;
          font-size: 20px;
          color: #333;
        }

        .logo-text p {
          margin: 2px 0 0;
          font-size: 12px;
          color: #666;
        }

        .logout-btn {
          background: #dc3545;
          color: white;
          border: none;
          padding: 10px 20px;
          border-radius: 8px;
          cursor: pointer;
          font-weight: 600;
          transition: all 0.3s;
        }

        .logout-btn:hover {
          background: #c82333;
          transform: translateY(-2px);
        }

        /* Stats Grid */
        .stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 15px;
          margin-bottom: 25px;
        }

        .stat-card {
          background: rgba(255,255,255,0.95);
          border-radius: 12px;
          padding: 15px;
          display: flex;
          align-items: center;
          gap: 15px;
          transition: transform 0.3s;
        }

        .stat-card:hover {
          transform: translateY(-3px);
        }

        .stat-icon {
          font-size: 36px;
        }

        .stat-info {
          flex: 1;
        }

        .stat-value {
          font-size: 28px;
          font-weight: bold;
          color: #333;
        }

        .stat-label {
          font-size: 12px;
          color: #666;
          margin-top: 4px;
        }

        /* Main Content */
        .admin-main {
          display: grid;
          grid-template-columns: 1fr 1.5fr;
          gap: 20px;
        }

        /* Create Area Card */
        .create-area-card, .areas-list-card {
          background: rgba(255,255,255,0.95);
          border-radius: 15px;
          overflow: hidden;
        }

        .card-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 15px 20px;
          background: #f8f9fa;
          border-bottom: 1px solid #e0e0e0;
          cursor: pointer;
        }

        .card-header h2 {
          margin: 0;
          font-size: 18px;
          color: #333;
        }

        .toggle-btn, .search-box input {
          padding: 8px 12px;
          border: 1px solid #ddd;
          border-radius: 8px;
          font-size: 14px;
        }

        .toggle-btn {
          background: #e0e0e0;
          cursor: pointer;
          font-size: 18px;
          font-weight: bold;
        }

        .search-box input {
          width: 200px;
        }

        /* Create Form */
        .create-form {
          padding: 20px;
        }

        .form-group {
          margin-bottom: 15px;
        }

        .form-group label {
          display: block;
          margin-bottom: 5px;
          font-weight: 600;
          color: #555;
          font-size: 13px;
        }

        .form-group input {
          width: 100%;
          padding: 10px;
          border: 1px solid #ddd;
          border-radius: 8px;
          font-size: 14px;
        }

        .form-group input:focus {
          outline: none;
          border-color: #667eea;
        }

        .create-btn {
          width: 100%;
          padding: 12px;
          background: linear-gradient(135deg, #28a745, #20c997);
          color: white;
          border: none;
          border-radius: 8px;
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s;
        }

        .create-btn:hover {
          transform: translateY(-2px);
        }

        .create-btn:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        /* Desktop Table */
        .desktop-view {
          display: block;
          overflow-x: auto;
        }

        .areas-table {
          width: 100%;
          border-collapse: collapse;
        }

        .areas-table th,
        .areas-table td {
          padding: 12px;
          text-align: left;
          border-bottom: 1px solid #f0f0f0;
        }

        .areas-table th {
          background: #f8f9fa;
          font-weight: 600;
          color: #555;
        }

        .area-name {
          font-weight: 600;
          color: #667eea;
        }

        .slug {
          font-family: monospace;
          font-size: 12px;
          color: #666;
        }

        .customer-count {
          text-align: center;
          font-weight: 600;
          color: #28a745;
        }

        .open-area-btn {
          padding: 6px 16px;
          background: linear-gradient(135deg, #667eea, #764ba2);
          color: white;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          font-size: 12px;
          transition: all 0.3s;
        }

        .open-area-btn:hover {
          transform: translateX(2px);
        }

        /* Mobile View */
        .mobile-view {
          display: none;
          padding: 15px;
        }

        .area-card {
          background: white;
          border: 1px solid #e0e0e0;
          border-radius: 12px;
          padding: 15px;
          margin-bottom: 12px;
        }

        .area-card-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 12px;
          padding-bottom: 10px;
          border-bottom: 1px solid #f0f0f0;
        }

        .area-card-header h3 {
          margin: 0;
          font-size: 16px;
          color: #667eea;
        }

        .customer-badge {
          background: #e8f5e9;
          color: #28a745;
          padding: 4px 8px;
          border-radius: 20px;
          font-size: 11px;
          font-weight: 600;
        }

        .area-card-details {
          margin-bottom: 12px;
        }

        .detail-row {
          display: flex;
          justify-content: space-between;
          margin-bottom: 8px;
          font-size: 13px;
        }

        .detail-row code {
          background: #f0f0f0;
          padding: 2px 6px;
          border-radius: 4px;
          font-size: 11px;
        }

        .open-area-btn-mobile {
          width: 100%;
          padding: 10px;
          background: linear-gradient(135deg, #667eea, #764ba2);
          color: white;
          border: none;
          border-radius: 8px;
          cursor: pointer;
          font-weight: 600;
        }

        /* Loading State */
        .loading-state {
          text-align: center;
          padding: 40px;
        }

        .spinner {
          width: 40px;
          height: 40px;
          border: 4px solid #f3f3f3;
          border-top: 4px solid #667eea;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin: 0 auto 15px;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        /* Message Toast */
        .message-toast {
          position: fixed;
          bottom: 20px;
          left: 50%;
          transform: translateX(-50%);
          padding: 12px 24px;
          border-radius: 10px;
          color: white;
          font-weight: 600;
          z-index: 1000;
          animation: slideUp 0.3s ease;
          white-space: nowrap;
        }

        .message-toast.success {
          background: #28a745;
        }

        .message-toast.error {
          background: #dc3545;
        }

        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateX(-50%) translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateX(-50%) translateY(0);
          }
        }

        .empty-state {
          text-align: center;
          padding: 40px;
          color: #999;
        }

        /* Responsive */
        @media (max-width: 1024px) {
          .admin-main {
            grid-template-columns: 1fr;
          }
        }

        @media (max-width: 768px) {
          .admin-dashboard {
            padding: 10px;
          }

          .header-content {
            flex-direction: column;
            text-align: center;
          }

          .stats-grid {
            grid-template-columns: repeat(3, 1fr);
            gap: 10px;
          }

          .stat-card {
            padding: 10px;
          }

          .stat-icon {
            font-size: 24px;
          }

          .stat-value {
            font-size: 20px;
          }

          .desktop-view {
            display: none;
          }

          .mobile-view {
            display: block;
          }

          .search-box input {
            width: 150px;
            font-size: 12px;
          }

          .card-header h2 {
            font-size: 16px;
          }
        }

        @media (max-width: 480px) {
          .stats-grid {
            grid-template-columns: 1fr;
          }

          .message-toast {
            white-space: normal;
            text-align: center;
            max-width: 90%;
          }
        }
      `}</style>
    </div>
  );
}